import { siteConfig } from '@/lib/site/config'
import { integrationData } from './data'
import { getIntegrationTypeLabel, type IntegrationMeta } from './types'

/**
 * Build the absolute URL for a given path on this site. Mirrors the helper
 * inside `lib/site/config.tsx` so this module stays a leaf and doesn't pull
 * in the JSX-heavy SEO module.
 */
function siteUrl(): string {
  return siteConfig.url.replace(/\/$/, '')
}

/**
 * `ItemList` JSON-LD describing the integrations catalog.
 *
 * Why ItemList:
 *  - Search engines treat ItemList as a legitimate "list of things"
 *    (Google supports it for carousels and rich results).
 *  - Generative answer engines (Perplexity, ChatGPT browse, Gemini)
 *    consume JSON-LD heavily for "list of integrations" style queries
 *    ("what does Brew integrate with?"), so this is the highest-leverage
 *    SEO + GEO signal we can add for this page.
 *
 * Each item is a `SoftwareApplication` so engines can resolve which Brew
 * provider wraps the third-party tool, with a deep link back to the
 * per-provider URL on this site.
 */
export function getIntegrationsItemListJsonLd() {
  const base = siteUrl()
  const items = integrationData.map((integration, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'SoftwareApplication',
      name: `${integration.name} integration`,
      url: `${base}/integrations/${integration.id}`,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: integration.description,
      isAccessibleForFree: true,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      additionalProperty: [
        {
          '@type': 'PropertyValue',
          name: 'Authentication',
          value: getIntegrationTypeLabel(integration.integrationType),
        },
        {
          '@type': 'PropertyValue',
          name: 'Data direction',
          value:
            integration.dataDirection === 'data_in'
              ? 'Inbound (data into Brew)'
              : 'Outbound (Brew exports to)',
        },
        {
          '@type': 'PropertyValue',
          name: 'Availability',
          value:
            integration.availability === 'coming_soon'
              ? 'Coming soon'
              : 'Available',
        },
      ],
    },
  }))

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${base}/integrations#catalog`,
    name: 'Brew integrations',
    description:
      'Catalog of OAuth and API-key integrations available in Brew. Pull design files, payments, and feedback into Brew, or push generated emails to your existing ESP.',
    url: `${base}/integrations`,
    numberOfItems: integrationData.length,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement: items,
  }
}

/**
 * Cross-page breadcrumb for `/integrations` so search engines render the
 * full path on the SERP. Cheap to add; large UX win on Google.
 */
export function getIntegrationsBreadcrumbJsonLd() {
  const base = siteUrl()
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: base,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Integrations',
        item: `${base}/integrations`,
      },
    ],
  }
}

/**
 * Per-integration `SoftwareApplication` JSON-LD for the dedicated
 * `/integrations/<provider>` page. Engines can resolve the page as a
 * concrete software entry rather than a generic catalog item, which
 * helps generative answer engines cite the right URL when asked
 * questions like "does Brew support Stripe webhooks?".
 */
export function getIntegrationSoftwareJsonLd(integration: IntegrationMeta) {
  const base = siteUrl()
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${base}/integrations/${integration.id}`,
    name: `${integration.name} integration`,
    url: `${base}/integrations/${integration.id}`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: integration.description,
    isAccessibleForFree: true,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Authentication',
        value: getIntegrationTypeLabel(integration.integrationType),
      },
      {
        '@type': 'PropertyValue',
        name: 'Data direction',
        value:
          integration.dataDirection === 'data_in'
            ? 'Inbound (data into Brew)'
            : 'Outbound (Brew exports to)',
      },
      {
        '@type': 'PropertyValue',
        name: 'Availability',
        value:
          integration.availability === 'coming_soon'
            ? 'Coming soon'
            : 'Available',
      },
    ],
  }
}

/**
 * Per-integration breadcrumb extending the catalog breadcrumb with the
 * specific provider as the third crumb.
 */
export function getIntegrationBreadcrumbJsonLd(integration: IntegrationMeta) {
  const base = siteUrl()
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: base,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Integrations',
        item: `${base}/integrations`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: integration.name,
        item: `${base}/integrations/${integration.id}`,
      },
    ],
  }
}
